import { Injectable } from '@angular/core';

@Injectable()
export class FiCompareHtmlService {
    compareNodes(sourceNode: Node, newNode: Node) {
        let newNodePointer: Node = newNode.firstChild;
        if (newNodePointer && !newNodePointer.textContent.trim().length) {
            newNodePointer = this.findNextNotEmptyBlock(newNodePointer);
        }

        sourceNode.childNodes.forEach((childNode, parent) => {
            // We need to skip all nodes except text and element from source html
            const ignoredNodeTypes: number[] = [Node.ELEMENT_NODE, Node.TEXT_NODE];
            if (ignoredNodeTypes.indexOf(childNode.nodeType) == -1) {
                return;
            }
            // Skip blocks, that contains only spaces (<br/>)
            if (!childNode.textContent.trim().length) {
                return;
            }
            // If there are no new nodes left in new html, mark current node in source html as deleted and move to
            // the next one
            if (!newNodePointer) {
                this.wrapAllTextNodes(childNode, 'del');
                return;
            }
            // For each element from source html we need to find exactly the same in new html
            const newSameNode = this.findNextSameNode(childNode, newNodePointer);
            // If there is an element which has the same content - mark all nodes before as inserted
            // and move pointer to the next one
            if (newSameNode) {
                while (newNodePointer !== newSameNode) {
                    const temp = newNodePointer;
                    newNodePointer = this.findNextNotEmptyBlock(newNodePointer);
                    this.wrapAllTextNodes(temp, 'ins');
                }
                newNodePointer = this.findNextNotEmptyBlock(newNodePointer);
                return;
            }
            // If the same node hasn't bee found in new html, try to find next one in old html
            // If next node from new html exists in old html - mark current in old html as deleted
            const oldSameNode = this.findNextSameNode(newNodePointer, childNode);
            if (oldSameNode) {
                this.wrapAllTextNodes(childNode, 'del');
                return;
            }

            // If the same block wasn't find - try to compare current blocks
            // Compare element nodes
            if (childNode.nodeType === Node.ELEMENT_NODE && newNodePointer.nodeType === Node.ELEMENT_NODE) {
                this.compareNodes(childNode, newNodePointer);
                newNodePointer = this.findNextNotEmptyBlock(newNodePointer);
                return;
            }
            // Compare text nodes
            if (childNode.nodeType === Node.TEXT_NODE && newNodePointer.nodeType === Node.TEXT_NODE) {
                this.compareTextNodes(childNode, newNodePointer, 'del');
                this.compareTextNodes(newNodePointer, childNode, 'ins');
                newNodePointer = newNodePointer.nextSibling;
                return;
            }

            // If node from old html wasn't find withing nodes in new html and current pointers have different types
            // (all previous conditions missed) - mark current node as deleted
            this.wrapAllTextNodes(childNode, 'del');
        });
        // insert blocks from new node that left after iterating old one
        if (newNodePointer) {
            do {
                const temp = newNodePointer;
                newNodePointer = this.findNextNotEmptyBlock(newNodePointer);
                this.wrapAllTextNodes(temp, 'ins');
            } while (newNodePointer);
        }
    }

    private findNextSameNode(node: Node, nextNode: Node): Node {
        do {
            if (this.isNodesEqual(node, nextNode)) return nextNode;
            nextNode = nextNode.nextSibling;
        } while (nextNode);
        return undefined;
    }

    private isNodesEqual(node: Node, nextNode: Node): boolean {
        return this.isElementNodesEqual(node, nextNode) || this.isTextNodesEqual(node, nextNode);
    }

    private isTextNodesEqual(node: Node, nextNode: Node): boolean {
        const nodesTypes = [node.nodeType, nextNode.nodeType];
        return nodesTypes.every((nodeType) => nodeType === Node.TEXT_NODE) && node.textContent === nextNode.textContent;
    }

    private isElementNodesEqual(node: Node, nextNode: Node): boolean {
        const nodesTypes = [node.nodeType, nextNode.nodeType];
        return (
            nodesTypes.every((nodeType) => nodeType === Node.ELEMENT_NODE) &&
            (node as HTMLElement).innerHTML === (nextNode as HTMLElement).innerHTML
        );
    }

    private findNextNotEmptyBlock(currentElement: Node): Node {
        while ((currentElement = currentElement.nextSibling)) {
            if (currentElement.textContent.trim().length) {
                return currentElement;
            }
        }
        return undefined;
    }

    private compareTextNodes(sourceNode: Node, newNode: Node, tag: string) {
        const sourceWords = sourceNode.textContent.split(' ');
        const newWords = newNode.textContent.split(' ');

        let newSourceText = '';
        let sourceOffset = 0;
        const removedParts = [];
        sourceWords.forEach((word) => {
            const newIndex = newWords.indexOf(word, sourceOffset);
            if (newIndex > -1) {
                sourceOffset = newIndex + 1;
                if (removedParts.length) {
                    newSourceText += '<' + tag + '>' + removedParts.join(' ') + '</' + tag + '> ';
                    removedParts.length = 0;
                }
                newSourceText += word + ' ';
            } else {
                removedParts.push(word);
            }
        });

        if (removedParts.length) {
            newSourceText += '<' + tag + '>' + removedParts.join(' ') + '</' + tag + '>';
        }

        const wrapperSpanEl = document.createElement('span');
        wrapperSpanEl.innerHTML = newSourceText;
        sourceNode.parentNode.replaceChild(wrapperSpanEl, sourceNode);
    }

    private wrapCheckNode(node: Node, tag: string): boolean {
        if (node.nodeType === Node.TEXT_NODE) {
            const wrapperEl = document.createElement(tag);
            const nodeParentNode = node.parentNode;
            wrapperEl.appendChild(node);
            nodeParentNode.appendChild(wrapperEl);
            return true;
        }
        return false;
    }
    private wrapAllTextNodes(parentNode: Node, tagName: string) {
        if (!this.wrapCheckNode(parentNode, tagName)) {
            parentNode.childNodes.forEach((node) => {
                this.wrapAllTextNodes(node, tagName);
            });
        }
    }
}
